<template>
  <div class="profile-page">
    <box :title="t('Meu Perfil')" type="primary">
      <cartao-da-implantacao />
      <div class="profile-content">
        <!-- User Information Section -->
        <div class="profile-section">
          <h3 class="section-title">{{ t('Informações do Usuário') }}</h3>

          <div class="profile-field">
            <label class="field-label">{{ t('Nome de Usuário') }}</label>
            <div class="field-value">{{ userData.username || t('Não disponível') }}</div>
          </div>

          <div class="profile-field">
            <label class="field-label">{{ t('Nome') }}</label>
            <div class="field-value">{{ userData.name || t('Não disponível') }}</div>
          </div>

          <div class="profile-field">
            <label class="field-label">{{ t('Email') }}</label>
            <div class="field-value">{{ userData.email || t('Não disponível') }}</div>
          </div>

          <div class="profile-field">
            <label class="field-label">{{ t('Email Verificado') }}</label>
            <div class="field-value">
              <Tag
                :value="userData.email_verified ? t('Sim') : t('Não')"
                :severity="userData.email_verified ? 'success' : 'warning'"
              />
            </div>
          </div>

          <div class="profile-field">
            <label class="field-label">{{ t('Idioma') }}</label>
            <div class="field-value">{{ languageDisplay }}</div>
          </div>
        </div>

        <!-- User Groups Section -->
        <div class="profile-section" v-if="userData.groups && userData.groups.length > 0">
          <h3 class="section-title">{{ t('Grupos') }}</h3>
          <div class="groups-container">
            <Tag
              v-for="group in userData.groups"
              :key="group"
              :value="group"
              severity="info"
              class="group-tag"
            />
          </div>
        </div>

        <!-- Franchise Information Section -->
        <div class="profile-section" v-if="franchiseInfo && franchiseInfo.length > 0">
          <h3 class="section-title">{{ t('Minhas Franquias') }}</h3>
          <ul class="franchise-list">
            <li v-for="franchise in franchiseInfo" :key="franchise.id" class="franchise-row">
              <span class="franchise-id">{{ franchise.id }}</span>
              <span class="franchise-name">{{ franchise.name }}</span>
            </li>
          </ul>
        </div>
      </div>
      <meus-dados-pessoais />
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue';
import CartaoDaImplantacao from '../implantacao/CartaoDaImplantacao.vue';
import MeusDadosPessoais from './MeusDadosPessoais.vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';

interface UserData {
  username?: string;
  name?: string;
  email?: string;
  email_verified?: boolean;
  language?: string;
  groups?: string[];
}

interface FranchiseRow {
  id: number | string;
  name: string;
}

interface FranchiseSource {
  ID: number | string;
  Name: string;
}

interface PermissionsServiceLike {
  getPermissionsData: () => { user?: UserData } | null | undefined;
  getFranquiasSelectArray: () => FranchiseSource[] | null | undefined;
}

const { t } = useI18n();

const permissionsService = inject<PermissionsServiceLike>('permissionsService');

const userData = ref<UserData>({});
const franchiseInfo = ref<FranchiseRow[]>([]);

const languageDisplay = computed<string>(() => {
  const languageMap: Record<string, string> = {
    'pt-BR': 'Português (Brasil)',
    'es-ES': 'Español (España)',
    'en-US': 'English (United States)',
    'en-GB': 'English (United Kingdom)',
    'pt-PT': 'Português (Portugal)',
    'es-GT': 'Español (Guatemala)',
    'pt-AO': 'Português (Angola)'
  };

  const lang = userData.value.language;
  return (lang && languageMap[lang]) || lang || t('Não disponível');
});

const loadProfileData = (): void => {
  log('ProfilePage', 'loadProfileData', 'Loading user profile data...');

  const permissions = permissionsService?.getPermissionsData();

  if (permissions && permissions.user) {
    userData.value = permissions.user;
    log('ProfilePage', 'loadProfileData', 'User data loaded:', userData.value);
  } else {
    log('ProfilePage', 'loadProfileData', 'No user data available');
  }

  const franchises = permissionsService?.getFranquiasSelectArray();
  if (franchises && franchises.length > 0) {
    franchiseInfo.value = franchises.map((f) => ({
      id: f.ID,
      name: f.Name
    }));
    log('ProfilePage', 'loadProfileData', 'Franchise data loaded:', franchiseInfo.value.length);
  }
};

onMounted(() => {
  log('ProfilePage', 'onMounted', 'Profile page mounted');
  loadProfileData();
});
</script>

<style scoped>
.profile-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.profile-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #3498db;
}

.profile-field {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 20px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.profile-field:last-child {
  border-bottom: none;
}

.field-label {
  font-weight: 600;
  color: #34495e;
  display: flex;
  align-items: center;
}

.field-value {
  color: #2c3e50;
  display: flex;
  align-items: center;
}

.groups-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.group-tag {
  font-size: 14px;
}

.franchise-list {
  list-style: none;
  margin: 10px 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.franchise-row {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 16px;
  padding: 10px 14px;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

.franchise-row:last-child {
  border-bottom: none;
}

.franchise-id {
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.franchise-name {
  color: #1f2937;
  font-weight: 500;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .profile-page {
    padding: 10px;
  }

  .profile-field {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .field-label {
    font-size: 14px;
  }

  .field-value {
    padding-left: 10px;
  }
}
</style>
